import React from 'react'
import Icon from '@mdi/react'
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiTools,
  mdiViewList,
} from '@mdi/js'
import '../../skins/oviyamng.css'
import '../../skins/studio.css'
import OviyamNgHeader from './OviyamNgHeader'
import OviyamNgSidebar from './OviyamNgSidebar'
import StudioToolsRail from '../studio/StudioToolsRail'

/**
 * Shell OviyamNG: toolbar Mayam + series left + viewport + tools right (gated).
 * children = viewport clínico — intocable.
 */
function OviyamNgShell({
  planLabel,
  chromeTheme = 'dark',
  title,
  subtitle,
  patientId,
  isFullscreen,
  toolActive,
  anyOpen,
  showMenu = true,
  sidebarCollapsed = false,
  toolsCollapsed = false,
  hideToolsRail = false,
  onToggleMenu,
  onToggleSidebar,
  onToggleToolsRail,
  onLayout,
  onToggleFullscreen,
  toolExecute,
  onReset,
  onPresets,
  showPresets = true,
  onCine,
  showCine = false,
  cineActive = false,
  onCaptureViewport,
  onShareViewport,
  onHeader,
  portalChrome = null,
  viewportWidth = 1440,
  bell,
  loadIndicator = null,
  studyLoadSource = null,
  onSelectSeries,
  autoSelectFirstSeries,
  activeSliceIndex = 0,
  toolsProps = null,
  voiceOpen = false,
  voicePanel = null,
  copilotOpen = false,
  copilotPanel = null,
  showRelatedStudies = false,
  relatedStudiesCount = 0,
  onRelatedStudies,
  showWorklist = false,
  worklistCount = 0,
  onWorklist,
  collapsedStudyKeys,
  onToggleStudyCollapse,
  viewportStudyUids,
  suggestedPriorSeriesKey,
  onOpenSuggestedInCompare,
  children,
}) {
  const theme =
    chromeTheme === 'brand' || chromeTheme === 'light' ? chromeTheme : 'dark'

  const effectiveToolsCollapsed = hideToolsRail || toolsCollapsed
  const viewportRef = React.useRef(null)

  // Cornerstone escucha `resize`: solo si el área útil cambió de verdad
  // (evitar spam de resize → scroll trabado).
  React.useEffect(() => {
    const el = viewportRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    let timer = null
    let lastW = -1
    let lastH = -1
    const notify = (w, h) => {
      if (Math.abs(w - lastW) < 2 && Math.abs(h - lastH) < 2) return
      lastW = w
      lastH = h
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        try {
          window.dispatchEvent(new Event('resize'))
        } catch (err) {
          /* ignore */
        }
      }, 80)
    }
    const ro = new ResizeObserver((entries) => {
      const cr = entries && entries[0] && entries[0].contentRect
      if (!cr) return
      notify(cr.width, cr.height)
    })
    ro.observe(el)
    notify(el.clientWidth, el.clientHeight)
    return () => {
      if (timer) clearTimeout(timer)
      ro.disconnect()
    }
  }, [sidebarCollapsed, effectiveToolsCollapsed, hideToolsRail])

  const shellClass = [
    'udv-oviyamng-shell',
    sidebarCollapsed ? 'is-sidebar-collapsed' : '',
    effectiveToolsCollapsed ? 'is-tools-collapsed' : '',
    hideToolsRail ? 'is-portal-no-tools' : '',
    voiceOpen ? 'is-voice-overlay' : '',
    copilotOpen ? 'is-copilot-overlay' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={shellClass} data-skin="oviyamng" data-theme={theme}>
      <OviyamNgHeader
        planLabel={planLabel}
        toolActive={toolActive}
        isFullscreen={isFullscreen}
        showMenu={showMenu}
        anyOpen={anyOpen}
        sidebarCollapsed={sidebarCollapsed}
        toolsCollapsed={!!effectiveToolsCollapsed}
        hideToolsRail={!!hideToolsRail}
        onToggleMenu={onToggleMenu}
        onToggleSidebar={onToggleSidebar}
        onToggleToolsRail={hideToolsRail ? undefined : onToggleToolsRail}
        onLayout={onLayout}
        onToggleFullscreen={onToggleFullscreen}
        toolExecute={toolExecute}
        onPresets={onPresets}
        showPresets={showPresets}
        onCaptureViewport={onCaptureViewport}
        portalChrome={portalChrome}
        bell={bell}
        loadIndicator={loadIndicator}
        studyLoadSource={studyLoadSource}
        toolsProps={toolsProps}
      />
      <div className="udv-oviyamng-body">
        <OviyamNgSidebar
          title={title}
          subtitle={subtitle}
          patientId={patientId}
          onSelectSeries={onSelectSeries}
          autoSelectFirstSeries={autoSelectFirstSeries}
          activeSliceIndex={activeSliceIndex}
          showRelatedStudies={!!showRelatedStudies}
          relatedStudiesCount={relatedStudiesCount || 0}
          onRelatedStudies={onRelatedStudies}
          showWorklist={!!showWorklist}
          worklistCount={worklistCount || 0}
          onWorklist={onWorklist}
          collapsedStudyKeys={collapsedStudyKeys}
          onToggleStudyCollapse={onToggleStudyCollapse}
          viewportStudyUids={viewportStudyUids}
          suggestedPriorSeriesKey={suggestedPriorSeriesKey}
          onOpenSuggestedInCompare={onOpenSuggestedInCompare}
        />
        <div className="udv-oviyamng-viewport-wrap" ref={viewportRef}>
          <div className="udv-oviyamng-viewport-frame">{children}</div>
        </div>
        {!hideToolsRail ? (
          <aside
            className="udv-oviyamng-tools-rail"
            aria-label="Herramientas"
            aria-hidden={!!effectiveToolsCollapsed}
          >
            {!effectiveToolsCollapsed ? (
              <>
                <div className="udv-oviyamng-tools-rail-head">
                  <button
                    type="button"
                    className="udv-oviyamng-tools-rail-collapse"
                    onClick={onToggleToolsRail}
                    aria-label="Ocultar herramientas"
                    title="Ocultar herramientas"
                  >
                    <Icon path={mdiChevronRight} size={0.75} color="currentColor" />
                  </button>
                </div>
                {toolsProps ? (
                  <StudioToolsRail
                    {...toolsProps}
                    className="udv-oviyamng-tools-inner"
                  />
                ) : null}
              </>
            ) : null}
          </aside>
        ) : null}
      </div>

      {voiceOpen && voicePanel ? (
        <div className="udv-oviyamng-voice-overlay" aria-label="Notas de voz">
          {voicePanel}
        </div>
      ) : null}
      {copilotOpen && copilotPanel ? (
        <div className="udv-oviyamng-copilot-overlay" aria-label="UDV AI Copilot">
          {copilotPanel}
        </div>
      ) : null}

      {sidebarCollapsed && onToggleSidebar ? (
        <button
          type="button"
          className="udv-oviyamng-series-notch"
          onClick={onToggleSidebar}
          aria-label="Mostrar series"
          title="Series"
        >
          <span className="udv-oviyamng-series-notch-face">
            <Icon path={mdiViewList} size={0.72} color="currentColor" />
            <Icon path={mdiChevronRight} size={0.65} color="currentColor" />
          </span>
        </button>
      ) : null}

      {!hideToolsRail && effectiveToolsCollapsed && onToggleToolsRail ? (
        <button
          type="button"
          className="udv-oviyamng-tools-notch"
          onClick={onToggleToolsRail}
          aria-label="Mostrar herramientas"
          title="Herramientas"
        >
          <span className="udv-oviyamng-tools-notch-face">
            <Icon path={mdiTools} size={0.72} color="currentColor" />
            <Icon path={mdiChevronLeft} size={0.65} color="currentColor" />
          </span>
        </button>
      ) : null}
    </div>
  )
}

export default OviyamNgShell
